// Damper force/speed curve interpolation.
//
// Compression and rebound are independent curves, each driven by up to two
// adjusters (LSC/HSC for compression, LSR/HSR for rebound). A datapoint only
// contributes to a curve if it carries a non-null forces array for that
// direction (compressionForces / reboundForces).

const toNum = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};

const lerp = (a, b, t) => a + (b - a) * t;

const lerpArrays = (arrA, arrB, t) => arrA.map((v, i) => lerp(v, arrB[i], t));

// Linear interpolation along a single adjuster axis.
function interpolate1D(points, key, queryValue) {
  const sorted = [...points].sort((a, b) => a[key] - b[key]);
  const q = Math.max(sorted[0][key], Math.min(sorted[sorted.length - 1][key], queryValue));

  if (sorted.length === 1) return sorted[0].forces;

  let lower = sorted[0];
  let upper = sorted[sorted.length - 1];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i][key] <= q && q <= sorted[i + 1][key]) {
      lower = sorted[i];
      upper = sorted[i + 1];
      break;
    }
  }
  if (lower[key] === upper[key]) return lower.forces;
  const t = (q - lower[key]) / (upper[key] - lower[key]);
  return lerpArrays(lower.forces, upper.forces, t);
}

// Bilinear interpolation over two adjuster axes, with inverse-distance-weighted
// fallback when the surrounding grid isn't fully populated.
function interpolate2D(points, keyA, keyB, queryA, queryB) {
  const valuesA = [...new Set(points.map(p => p[keyA]))].sort((x, y) => x - y);
  const valuesB = [...new Set(points.map(p => p[keyB]))].sort((x, y) => x - y);

  const qA = Math.max(valuesA[0], Math.min(valuesA[valuesA.length - 1], queryA));
  const qB = Math.max(valuesB[0], Math.min(valuesB[valuesB.length - 1], queryB));

  const aLow = Math.max(...valuesA.filter(v => v <= qA));
  const aHigh = Math.min(...valuesA.filter(v => v >= qA));
  const bLow = Math.max(...valuesB.filter(v => v <= qB));
  const bHigh = Math.min(...valuesB.filter(v => v >= qB));

  const findPoint = (a, b) => points.find(p => p[keyA] === a && p[keyB] === b);
  const corners = {
    ll: findPoint(aLow, bLow),
    lh: findPoint(aLow, bHigh),
    hl: findPoint(aHigh, bLow),
    hh: findPoint(aHigh, bHigh),
  };

  if (corners.ll && corners.lh && corners.hl && corners.hh) {
    const tA = aHigh === aLow ? 0 : (qA - aLow) / (aHigh - aLow);
    const tB = bHigh === bLow ? 0 : (qB - bLow) / (bHigh - bLow);
    const low = lerpArrays(corners.ll.forces, corners.hl.forces, tA);
    const high = lerpArrays(corners.lh.forces, corners.hh.forces, tA);
    return lerpArrays(low, high, tB);
  }

  // Incomplete grid: fall back to inverse-distance-weighted average,
  // normalized by each axis's range so neither axis dominates.
  const rangeA = (valuesA[valuesA.length - 1] - valuesA[0]) || 1;
  const rangeB = (valuesB[valuesB.length - 1] - valuesB[0]) || 1;
  let weightSum = 0;
  let forcesSum = null;
  for (const p of points) {
    const dA = (p[keyA] - qA) / rangeA;
    const dB = (p[keyB] - qB) / rangeB;
    const dist = Math.hypot(dA, dB);
    const weight = dist < 1e-9 ? 1e9 : 1 / dist;
    weightSum += weight;
    const weighted = p.forces.map(f => f * weight);
    forcesSum = forcesSum ? forcesSum.map((v, i) => v + weighted[i]) : weighted;
  }
  return forcesSum.map(v => v / weightSum);
}

// Interpolates a single direction's curve (compression or rebound) for the
// given adjuster keys, query values, and forces field.
export function interpolateAdjusterCurve(datapoints, adjusterKeys, queryValues, forcesField) {
  const enabledKeys = adjusterKeys.filter(k => queryValues[k] !== undefined && queryValues[k] !== null);
  const qualifying = datapoints
    .filter(dp => Array.isArray(dp[forcesField]))
    .map(dp => ({
      ...enabledKeys.reduce((acc, k) => ({ ...acc, [k]: toNum(dp[k]) }), {}),
      forces: dp[forcesField].map(f => toNum(f)),
    }))
    .filter(dp => enabledKeys.every(k => dp[k] !== null) && dp.forces.every(f => f !== null));

  if (enabledKeys.length === 0 || qualifying.length === 0) return null;

  if (enabledKeys.length === 1) {
    const key = enabledKeys[0];
    if (new Set(qualifying.map(p => p[key])).size < 1) return null;
    return interpolate1D(qualifying, key, toNum(queryValues[key]) ?? 0);
  }

  const [keyA, keyB] = enabledKeys;
  const distinctA = new Set(qualifying.map(p => p[keyA])).size;
  const distinctB = new Set(qualifying.map(p => p[keyB])).size;
  if (distinctA <= 1 && distinctB <= 1) return qualifying[0].forces;
  if (distinctA <= 1) return interpolate1D(qualifying, keyB, toNum(queryValues[keyB]) ?? 0);
  if (distinctB <= 1) return interpolate1D(qualifying, keyA, toNum(queryValues[keyA]) ?? 0);

  return interpolate2D(qualifying, keyA, keyB, toNum(queryValues[keyA]) ?? 0, toNum(queryValues[keyB]) ?? 0);
}

// Builds both branches (compression, rebound) for one side (Front/Rear) of
// the damper, given the side's stored config and the live adjuster values
// resolved from session parameters.
export function buildDamperCurves(sideConfig, liveAdjusterValues) {
  const speeds = (sideConfig?.speeds || []).map(toNum).filter(s => s !== null);
  const datapoints = sideConfig?.datapoints || [];
  if (speeds.length === 0 || datapoints.length === 0) {
    return { speeds, compression: null, rebound: null };
  }

  const compression = interpolateAdjusterCurve(datapoints, ['LSC', 'HSC'], liveAdjusterValues, 'compressionForces');
  const rebound = interpolateAdjusterCurve(datapoints, ['LSR', 'HSR'], liveAdjusterValues, 'reboundForces');

  return { speeds, compression, rebound };
}
